<template>
  <div class="reading-calculator">
    <div class="reading-calculator__top-wave" />

    <div class="reading-calculator__container">
      <h3 class="reading-calculator__headline">
        {{ headline }}
      </h3>

      <div class="reading-calculator__calculator">
        <img
          class="reading-calculator__decrement-button"
          :src="decrementIcon"
          :alt="'Decrement number of books'"
          @click="changeBooksCount(-1)"
        />

        <div class="reading-calculator__books-count">
          {{ booksCount }}
        </div>

        <img
          class="reading-calculator__increment-button"
          :src="incrementIcon"
          :alt="'Increment number of books'"
          @click="changeBooksCount(1)"
        />
      </div>

      <div class="reading-calculator__usp" v-html="usp" />

      <div class="reading-calculator__final-duration">
        <div class="reading-calculator__hours">
          {{ animatedHours }}<span class="reading-calculator__hours-label">h</span>
        </div>
        <div class="reading-calculator__minutes">
          {{ animatedMinutes }}<span class="reading-calculator__minutes-label">min</span>
        </div>
      </div>

      <div class="reading-calculator__time-with-blinkist">
        {{ $t("timeWithBlinkist") }}
      </div>
    </div>

    <div class="reading-calculator__bottom-wave" />
  </div>
</template>

<script>
import decrementIcon from "@images/svgs/reading-calculator/decrement_icon.svg"
import incrementIcon from "@images/svgs/reading-calculator/increment_icon.svg"
import { tween } from "shifty"
import events from "@/bl-events"

const averageReadingTime = 15 // min

export default {
  name: "ReadingCalculator",
  props: {
    headline: {
      type: String,
      default: null
    },
    usp: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      booksCount: 1,
      tweenedHours: 0,
      tweenedMinutes: 15,
      decrementIcon: decrementIcon,
      incrementIcon: incrementIcon
    }
  },
  computed: {
    animatedHours() {
      return this.tweenedHours.toFixed(0).padStart(2, "0")
    },
    animatedMinutes() {
      return this.tweenedMinutes.toFixed(0).padStart(2, "0")
    }
  },
  watch: {
    booksCount: function (newValue, oldValue) {
      const totalDuration = newValue * averageReadingTime
      const minutes = totalDuration % 60
      const hours = (totalDuration - minutes) / 60

      const oldDuration = oldValue * averageReadingTime
      const oldMinutes = oldDuration % 60
      const oldHours = (oldDuration - oldMinutes) / 60

      if (hours - oldHours === 1) {
        tween({
          from: { num: this.tweenedMinutes },
          to: { num: 59 },
          easing: { num: "linear" },
          duration: 500,
          step: (state) => {
            if (state.num >= 59) {
              this.tweenedMinutes = 0
              this.tweenedHours = hours
            } else {
              this.tweenedMinutes = state.num
            }
          }
        })
      } else if (hours - oldHours === -1) {
        this.tweenedHours = hours
        this.tweenedMinutes = 59

        tween({
          from: { num: this.tweenedMinutes },
          to: { num: minutes },
          easing: { num: "linear" },
          duration: 500,
          step: (state) => {
            this.tweenedMinutes = state.num
          }
        })
      } else {
        tween({
          from: { num: this.tweenedMinutes },
          to: { num: minutes },
          easing: { num: "linear" },
          duration: 500,
          step: (state) => {
            this.tweenedMinutes = state.num
          }
        })
      }
    }
  },
  methods: {
    changeBooksCount(delta) {
      const piwikAction = delta > 0 ? "1-increment" : "1-decrement"
      events.track("body-20200104-reading_calculator", piwikAction, this.booksCount)

      if (this.booksCount + delta > 0 && this.booksCount + delta <= 99) {
        this.booksCount += delta
      }
    }
  },
  i18n: {
    messages: {
      en: {
        timeWithBlinkist: "Time spent with Blinkist"
      },
      de: {
        timeWithBlinkist: "Lesezeit mit Blinkist"
      }
    }
  }
}
</script>

<style lang="scss">
.reading-calculator {
  @include font-family(sans-serif);

  background-color: $summer-yellow;

  &__container {
    padding: $spacing-8 $spacing-16;
  }

  &__top-wave {
    height: 93px;

    background-image: url("~@images/svgs/reading-calculator/top_wave.svg");
    background-repeat: no-repeat;
    background-position: center left;
    background-size: contain;
  }

  &__bottom-wave {
    height: 93px;

    background-image: url("~@images/svgs/reading-calculator/bottom_wave.svg");
    background-repeat: no-repeat;
    background-position: center right;
    background-size: contain;
  }

  &__headline {
    @include font-style(h3);

    text-align: center;
  }

  &__calculator {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: $spacing-24;

    font-weight: font-weight(bold);
    font-size: spacing(40px);
    line-height: $spacing-48;
  }

  &__books-count {
    @include font-style(h1);

    margin: 0 $spacing-16;
  }

  &__decrement-button {
    cursor: pointer;
  }

  &__increment-button {
    cursor: pointer;
  }

  &__usp {
    @include font-style(caption, $color: $dark-grey);

    margin-bottom: $spacing-24;

    text-align: center;

    mark {
      color: $dark-grey;
    }
  }

  &__final-duration {
    @include font-style(h1);

    display: flex;
    justify-content: center;
    align-items: baseline;
    margin-bottom: $spacing-16;
  }

  &__hours {
    margin-right: $spacing-24;
  }

  &__hours-label {
    @include font-style(subheadline);

    margin-left: $spacing-8;
  }

  &__minutes-label {
    @include font-style(subheadline);

    margin-left: $spacing-8;
  }

  &__time-with-blinkist {
    @include font-style(caption, $color: $dark-grey);

    text-align: center;
  }
}
</style>
